import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { astrologyService } from '../services/astrologyService';
import { profileService } from '../services/profileService';
import { useAuth } from '../context/AuthContext';
import './Horoscope.css';

const signsData = [
  {name: "Aries", icon: "M8 20 L17 6 L26 20"},
  {name: "Taurus", icon: "M17 8 a9 9 0 1 0 0.1 0"},
  {name: "Gemini", icon: "M11 8 V26 M23 8 V26 M11 8 Q17 14 23 8 M11 26 Q17 20 23 26"},
  {name: "Cancer", icon: "M9 14 a5 5 0 1 0 6 -6 M25 20 a5 5 0 1 1 -6 6"},
  {name: "Leo", icon: "M17 8 a6 6 0 1 1 -0.1 0 M17 20 v6 M13 24 h8"},
  {name: "Virgo", icon: "M9 26 V12 h5 v14 M18 26 V12 h5 a4 4 0 0 1 0 8 h-5"},
  {name: "Libra", icon: "M9 22 h16 M12 22 a5 5 0 0 1 10 0 M9 26 h16"},
  {name: "Scorpio", icon: "M9 14 V22 a4 4 0 0 0 8 0 V10 M17 22 h6 l-3 -4 m3 4 l-3 4"},
  {name: "Sagittarius", icon: "M10 24 L24 10 M17 10 h7 v7 M10 24 l4 -1 m-4 1 l1 4"},
  {name: "Capricorn", icon: "M9 12 V22 a5 5 0 0 0 9 3 l7 -12"},
  {name: "Aquarius", icon: "M8 13 q4 -5 9 0 q5 5 9 0 M8 20 q4 -5 9 0 q5 5 9 0"},
  {name: "Pisces", icon: "M17 8 V26 M9 12 q8 5 0 10 M25 12 q-8 5 0 10"}
];

const Horoscope: React.FC = () => {
  const [zodiacSign, setZodiacSign] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [city, setCity] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [horoscopeResult, setHoroscopeResult] = useState<any>(null);
  const [cities, setCities] = useState<string[]>([]);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const loadCities = async () => {
      try {
        const data = await astrologyService.getCities();
        if (data.status === 'success') {
          setCities(data.cities);
        }
      } catch (err) {
        console.error('Failed to load cities:', err);
        setCities([
          "Delhi", "Mumbai", "Bangalore", "Hyderabad", "Chennai", "Kolkata", "Pune", "Ahmedabad",
          "Jaipur", "Lucknow", "Kochi", "Visakhapatnam", "Indore", "Chandigarh", "Nagpur", "Vadodara",
          "Aurangabad", "Coimbatore", "Trivandrum", "Ludhiana", "Varanasi", "Kanpur", "Prayagraj", "Patna",
          "Ranchi", "Bhopal", "Raipur", "Guwahati", "Ghaziabad", "Noida", "Gurugram", "Faridabad"
        ]);
      }
    };
    loadCities();
  }, []);

  useEffect(() => {
    const loadProfile = async () => {
      if (isAuthenticated) {
        const profile = await profileService.getProfile();
        if (profile) {
          if (profile.zodiac) setZodiacSign(profile.zodiac);
          if (profile.dob) setDate(profile.dob);
          if (profile.time) setTime(profile.time);
          if (profile.place) setCity(profile.place);
        }
      }
    };
    loadProfile();
  }, [isAuthenticated]);

  const handleGenerateHoroscope = async () => {
    if (!zodiacSign || !date || !time || !city) {
      setError('Please fill in all details');
      return;
    }

    setLoading(true);
    setError('');
    setHoroscopeResult(null);

    try {
      const result = await astrologyService.generateKundli({
        birth_date: date,
        birth_time: time,
        birth_city: city
      });

      if (result.status === 'success') {
        let text = result.horoscope || '';
        // Frontend parser: Extract actual text from stringified python dictionary if present
        const match = text.match(/'generated_texts':\s*\[\s*(.*?)\s*\]/s);
        if (match && match[1]) {
          let extracted = match[1].trim();
          // Remove wrapping quotes (single or double) from the extracted text
          if ((extracted.startsWith("'") && extracted.endsWith("'")) || 
              (extracted.startsWith('"') && extracted.endsWith('"'))) {
            extracted = extracted.slice(1, -1);
          }
          // Remove any escaping backslashes if present
          extracted = extracted.replace(/\\'/g, "'").replace(/\\"/g, '"');
          
          text = text.replace(/\{.*?'generated_texts'.*?\}/s, extracted);
        }
        setHoroscopeResult({ ...result, horoscope: text });
      } else {
        setError(result.error || 'Failed to generate horoscope');
      }
    } catch (err: any) {
      console.error('Horoscope Error:', err);
      setError(err.response?.data?.detail || err.message || 'Error connecting to astrology server.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="horoscope-redesign">
      <div className="wrap">
        <section className="page-head">
          <div className="eyebrow">Recomputed daily, at 00:00 IST</div>
          <h1>Today's reading, by chart — not just by sign.</h1>
          <p>Pick your sun sign for a fast read, or add your birth time later for a chart-level forecast that accounts for your rising and moon sign too.</p>
        </section>

        <div className="sign-grid">
          {signsData.map((s) => (
            <button 
              key={s.name} 
              className={`sign ${zodiacSign === s.name ? 'active' : ''}`}
              onClick={() => setZodiacSign(s.name)}
            >
              <svg viewBox="0 0 34 34">
                <path d={s.icon} />
              </svg>
              <span>{s.name.slice(0, 3)}</span>
            </button>
          ))}
        </div>

        <div className="form-container">
          {error && <div className="error-message">{error}</div>}
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="zodiacSign">ZODIAC SIGN</label>
              <select
                id="zodiacSign"
                value={zodiacSign}
                onChange={(e) => setZodiacSign(e.target.value)}
                className="form-select"
              >
                <option value="">Select your sign</option>
                {signsData.map((s) => (
                  <option key={s.name} value={s.name}>{s.name}</option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="date">BIRTH DATE</label>
              <input
                type="date"
                id="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="form-input"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="time">BIRTH TIME</label>
              <input
                type="time"
                id="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="form-input"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="city">CITY</label>
              <select
                id="city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="form-select"
              >
                <option value="">Select your city</option>
                {cities.map((cityName) => (
                  <option key={cityName} value={cityName}>{cityName}</option>
                ))}
              </select>
            </div>
          </div>
          
          <button 
            onClick={handleGenerateHoroscope} 
            className="btn btn-solid"
            disabled={loading}
          >
            {loading ? 'COMPUTING CHART...' : 'GET READING'}
          </button>
        </div>

        {horoscopeResult && (
          <div className="reading">
            <div className="reading-main">
              <div className="reading-tag">ANALYSIS · {zodiacSign.toUpperCase()}</div>
              <h2>Your Cosmic Forecast</h2>
              <div className="markdown-content">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {horoscopeResult.horoscope}
                </ReactMarkdown>
                
                {horoscopeResult.sections && (
                  <>
                    {horoscopeResult.sections.career && (
                      <div>
                        <h3>Career</h3>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{horoscopeResult.sections.career}</ReactMarkdown>
                      </div>
                    )}
                    {horoscopeResult.sections.finance && (
                      <div>
                        <h3>Finance</h3>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{horoscopeResult.sections.finance}</ReactMarkdown>
                      </div>
                    )}
                    {horoscopeResult.sections.relationships && (
                      <div>
                        <h3>Relationships</h3>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{horoscopeResult.sections.relationships}</ReactMarkdown>
                      </div>
                    )}
                    {horoscopeResult.sections.health && (
                      <div>
                        <h3>Health</h3>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{horoscopeResult.sections.health}</ReactMarkdown>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="reading-side">
              <div className="side-block">
                <div className="side-label">NATAL DETAILS</div>
                <div className="transit-row"><span>SUN SIGN</span><span>{horoscopeResult.sun_sign}</span></div>
                <div className="transit-row"><span>DATE</span><span>{horoscopeResult.birth_date}</span></div>
                <div className="transit-row"><span>TIME</span><span>{horoscopeResult.birth_time}</span></div>
                <div className="transit-row"><span>CITY</span><span>{horoscopeResult.birth_city}</span></div>
              </div>
              <div className="side-block">
                <div className="side-label">ENGINE STATUS</div>
                <div className="transit-row"><span>CONFIDENCE</span><span>94%</span></div>
                <div className="transit-row"><span>COMPUTED</span><span>JUST NOW</span></div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Horoscope;
